import { BACKDROP_SUBSCRIBE_KEY, hideBackdrop, showBackdrop } from "./backdrop-utils.js";
import {
  focusModalPanel,
  getOpenModal,
  restoreModalTriggerFocus,
  setAriaHidden,
  setBodyLocked,
  storeModalTrigger
} from "./modal-utils.js";

const OPEN_CLASS = "subscribe-modal--open";
const FORM_ERROR_CLASS = "subscribe-modal__form--error";
const CONTENT_SUCCESS_CLASS = "subscribe-modal__content--success";
const MODAL_SELECTOR = ".subscribe-modal";
const PANEL_SELECTOR = ".subscribe-modal__panel";

const isValidSubscribeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

const resetSubscribeModalFormState = (modal) => {
  const content = modal.querySelector(".subscribe-modal__content");
  const form = modal.querySelector("[data-subscribe-form]");
  const success = modal.querySelector(".subscribe-modal__success");

  content?.classList.remove(CONTENT_SUCCESS_CLASS);
  form?.classList.remove(FORM_ERROR_CLASS);
  form?.reset();
  success?.setAttribute("hidden", "");
};

const showSubscribeModalSuccess = (modal) => {
  const content = modal.querySelector(".subscribe-modal__content");
  const success = modal.querySelector(".subscribe-modal__success");

  content?.classList.add(CONTENT_SUCCESS_CLASS);
  success?.removeAttribute("hidden");
  success?.focus?.({ preventScroll: true });
};

const SUBSCRIBE_WILL_OPEN_EVENT = "nii:subscribe-modal-will-open";
const CLOSE_TRANSITION_MS = 420;

let pendingCloseModal = null;
let pendingCloseListener = null;
let pendingCloseTimer = null;

const cancelPendingSubscribeClose = () => {
  if (pendingCloseModal && pendingCloseListener) {
    pendingCloseModal.removeEventListener("transitionend", pendingCloseListener);
  }
  if (pendingCloseTimer !== null) {
    clearTimeout(pendingCloseTimer);
    pendingCloseTimer = null;
  }
  pendingCloseModal = null;
  pendingCloseListener = null;
};

const openSubscribeModal = (modal, documentReference, trigger) => {
  cancelPendingSubscribeClose();

  resetSubscribeModalFormState(modal);

  documentReference.dispatchEvent(new CustomEvent(SUBSCRIBE_WILL_OPEN_EVENT));

  modal.removeAttribute("hidden");

  // Дать браузеру отрисовать состояние без `--open`, иначе opacity/transform transition не стартует
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add(OPEN_CLASS);
      setAriaHidden(modal, false);
      setBodyLocked(documentReference, true);
      showBackdrop(BACKDROP_SUBSCRIBE_KEY, documentReference);
      focusModalPanel(modal, PANEL_SELECTOR);
      storeModalTrigger(modal, trigger);
    });
  });
};

const closeSubscribeModal = (modal, documentReference) => {
  if (!modal.classList.contains(OPEN_CLASS)) {
    return;
  }

  cancelPendingSubscribeClose();

  modal.classList.remove(OPEN_CLASS);
  setBodyLocked(documentReference, false);
  hideBackdrop(BACKDROP_SUBSCRIBE_KEY, documentReference);

  let finished = false;
  const completeClose = () => {
    if (finished) {
      return;
    }
    finished = true;

    const listener = pendingCloseListener;
    if (listener) {
      modal.removeEventListener("transitionend", listener);
    }
    if (pendingCloseTimer !== null) {
      clearTimeout(pendingCloseTimer);
      pendingCloseTimer = null;
    }
    pendingCloseModal = null;
    pendingCloseListener = null;

    if (modal.classList.contains(OPEN_CLASS)) {
      return;
    }

    modal.setAttribute("hidden", "");
    setAriaHidden(modal, true);
    restoreModalTriggerFocus(modal);
  };

  pendingCloseModal = modal;
  pendingCloseListener = (event) => {
    if (event.target !== modal) {
      return;
    }
    if (event.propertyName !== "opacity" && event.propertyName !== "transform") {
      return;
    }
    completeClose();
  };

  modal.addEventListener("transitionend", pendingCloseListener);
  pendingCloseTimer = window.setTimeout(completeClose, CLOSE_TRANSITION_MS);
};

export const initSubscribeModal = (documentReference = document) => {
  documentReference.addEventListener("submit", (event) => {
    const form = event.target.closest("[data-subscribe-form]");
    if (!form) return;

    const modal = form.closest(".subscribe-modal");
    if (!modal) return;

    event.preventDefault();

    const input = form.querySelector(".subscribe-modal__input");
    const raw = input?.value ?? "";

    if (!isValidSubscribeEmail(raw)) {
      form.classList.add(FORM_ERROR_CLASS);
      input?.focus();
      return;
    }

    form.classList.remove(FORM_ERROR_CLASS);

    showSubscribeModalSuccess(modal);
  });

  documentReference.addEventListener("input", (event) => {
    const input = event.target.closest?.(".subscribe-modal__input");
    if (!input) return;

    const form = input.closest("[data-subscribe-form]");
    if (!form) return;

    form.classList.remove(FORM_ERROR_CLASS);
  });

  documentReference.addEventListener("click", (event) => {
    const openHost = event.target.closest("[data-subscribe-modal-open]");
    if (openHost) {
      event.preventDefault();
      const targetId = openHost.getAttribute("data-subscribe-modal-open")?.trim();
      if (!targetId) return;

      const modal = documentReference.getElementById(targetId);
      if (!modal?.classList.contains("subscribe-modal")) return;

      openSubscribeModal(modal, documentReference, openHost);
      return;
    }

    if (event.target.closest("[data-subscribe-modal-close]")) {
      const modal = event.target.closest(".subscribe-modal");
      if (modal?.classList.contains(OPEN_CLASS)) {
        closeSubscribeModal(modal, documentReference);
      }
      return;
    }

    if (event.target.classList.contains("backdrop")) {
      const modal = getOpenModal(documentReference, MODAL_SELECTOR, OPEN_CLASS);
      if (modal) {
        closeSubscribeModal(modal, documentReference);
      }
    }
  });

  documentReference.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const modal = getOpenModal(documentReference, MODAL_SELECTOR, OPEN_CLASS);
    if (modal) {
      closeSubscribeModal(modal, documentReference);
    }
  });
};
